import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    Shield,
    Calendar,
    MessageSquare,
    AlertTriangle,
    Gavel,
    MapPin,
    Hash,
    Ticket,
    ExternalLink,
    Save,
    Edit3,
    Clock,
    User,
    CheckCircle2,
    AlertCircle,
    ScrollText,
    Copy,
    Check,
    Ban,
    History,
    FileVideo,
    RefreshCw,
    Trash2,
    Wrench,
    FileImage,
    Link,
    CalendarDays,
    FileText,
    ShieldAlert,
    RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import R2Upload from './R2Upload';
import { PUNISHMENT_TYPES, calculateExpiration } from '../../utils/punishmentUtils';
import { useTickets } from '../../contexts/TicketContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';

const PlayerProfileView = ({ player, onClose }) => {
    const navigate = useNavigate();
    const { tickets } = useTickets();
    const { getAllPunishments, revokePunishment: contextRevokePunishment, addPunishment, deletePunishment: contextDeletePunishment } = usePunishment();
    const { user: currentUser } = useAuth();

    const [staffNotes, setStaffNotes] = useState('');
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [tempNotes, setTempNotes] = useState('');
    const [idCopied, setIdCopied] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0); // Force re-render trigger

    // Punishment Form State
    const [isPunishModalOpen, setIsPunishModalOpen] = useState(false);
    const [isApplyingPunishment, setIsApplyingPunishment] = useState(false);
    const [punishmentForm, setPunishmentForm] = useState({
        type: 'Advertência',
        reason: '',
        duration: '',
        durationType: 'days',
        isIndefinite: false,
        notes: '',
        selectedCharacterIdx: 'all', // Index in player.cities or 'all'
        attachments: []
    });

    // Derived Data - recalculated on refreshKey change
    const isStaff = player.accessLevel && player.accessLevel !== 'user';
    const allPlayerPunishments = (getAllPunishments() || []).filter(p =>
        p.userId === player.discordId
    ).sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt));

    const activePunishments = allPlayerPunishments.filter(p =>
        p.status === 'active' && (!p.expiresAt || new Date(p.expiresAt) > new Date())
    );

    const isTicketBanned = activePunishments.some(p =>
        p.punishmentType === 'Blacklist de Tickets' || p.publicReason?.toLowerCase().includes('ticket')
    );

    // Custom revoke function that triggers refresh
    const revokePunishment = async (id, revokedBy, reason) => {
        await contextRevokePunishment(id, revokedBy, reason);
        setTimeout(() => setRefreshKey(prev => prev + 1), 100);
    };

    const handleDeletePunishment = async (id) => {
        if (window.confirm('Tem certeza que deseja apagar permanentemente este registro? Esta ação não pode ser desfeita.')) {
            await contextDeletePunishment(id);
            toast.success('Registro apagado permanentemente.');
            setTimeout(() => setRefreshKey(prev => prev + 1), 100);
        }
    };

    // Unified History: User Tickets + Escalations involving user
    const playerTickets = tickets.filter(t =>
        String(t.userId) === String(player.discordId) ||
        String(t.userDiscordId) === String(player.discordId) ||
        (t.authorName && player.name && t.authorName.toLowerCase() === player.name.toLowerCase())
    ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const summons = playerTickets.filter(t =>
        t.isSummon === true ||
        t.title?.toUpperCase().includes('INTIMAÇÃO') ||
        t.auxiliaryStatus?.includes('intimacao')
    );

    useEffect(() => {
        const fetchStaffNotes = async () => {
            try {
                const { data, error } = await supabase
                    .from('staff_notes')
                    .select('notes')
                    .eq('user_id', player.discordId)
                    .single();

                if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
                    console.error('Error fetching staff notes:', error);
                } else {
                    const notes = data?.notes || '';
                    setStaffNotes(notes);
                    setTempNotes(notes);
                }
            } catch (err) {
                console.error('Unexpected error loading notes:', err);
            }
        };

        if (player.discordId) {
            fetchStaffNotes();
        }
    }, [player.discordId]);

    const handleSaveNotes = async () => {
        try {
            const { error } = await supabase
                .from('staff_notes')
                .upsert({
                    user_id: player.discordId,
                    notes: tempNotes,
                    last_updated_by: currentUser?.name || 'Staff',
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;

            setStaffNotes(tempNotes);
            setIsEditingNotes(false);
            toast.success('Notas salvas com sucesso!');
        } catch (error) {
            console.error(error);
            toast.error('Erro ao salvar notas.');
        }
    };

    const handleCopyId = () => {
        navigator.clipboard.writeText(player.discordId);
        setIdCopied(true);
        setTimeout(() => setIdCopied(false), 2000);
        toast.success('ID copiado para a área de transferência!');
    };

    const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return 'N/A';
        const options = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        };
        if (includeTime) {
            options.hour = '2-digit';
            options.minute = '2-digit';
        }
        return new Date(dateString).toLocaleDateString('pt-BR', options);
    };

    const getRoleBadgeColor = (level) => {
        if (['admin', 'direcao'].includes(level)) return 'bg-red-500/20 text-red-400 border-red-500/30';
        if (level?.includes('head')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
        if (level !== 'user') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    };

    const handlePunishSubmit = async () => {
        if (!punishmentForm.reason || !punishmentForm.notes) {
            toast.error('Informe o motivo público e as notas internas.');
            return;
        }

        const needsDuration = !punishmentForm.isIndefinite &&
            punishmentForm.type !== 'Blacklist de Tickets' &&
            punishmentForm.type !== 'Banimento Permanente';

        if (needsDuration && (!punishmentForm.duration || punishmentForm.duration <= 0)) {
            toast.error('Informe uma duração válida.');
            return;
        }

        let targetChar = null;
        if (punishmentForm.selectedCharacterIdx !== 'all') {
            targetChar = player.cities[parseInt(punishmentForm.selectedCharacterIdx)];
        }

        setIsApplyingPunishment(true);
        try {
            const punishmentData = {
                userId: player.discordId,
                punishmentType: punishmentForm.type,
                characterId: targetChar?.playerId || null,
                characterName: targetChar?.characterName || (punishmentForm.type === 'Blacklist de Tickets' ? 'N/A - Bloqueio de Tickets' : player.name),
                city: targetChar?.city || null,
                publicReason: punishmentForm.reason,
                internalReason: punishmentForm.notes,
                appliedBy: currentUser?.name || 'Staff',
                expiresAt: calculateExpiration(punishmentForm.duration, punishmentForm.durationType, punishmentForm.type, punishmentForm.isIndefinite),
                attachments: punishmentForm.attachments,
                userIP: player.ip || null,
                userEmail: player.email || null,
                userPhone: player.phone || null
            };

            await addPunishment(punishmentData);
            toast.success(`Punição "${punishmentForm.type}" aplicada com sucesso para ${player.name}.`);
            setIsPunishModalOpen(false);

            // Reset form
            setPunishmentForm({
                type: 'Advertência',
                reason: '',
                duration: '',
                durationType: 'days',
                isIndefinite: false,
                notes: '',
                selectedCharacterIdx: 'all',
                attachments: []
            });

            // Auto-refresh
            setTimeout(() => setRefreshKey(prev => prev + 1), 500);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao aplicar punição.');
        } finally {
            setIsApplyingPunishment(false);
        }
    };

    return (
        <div className="w-full h-full bg-[#09090b] text-zinc-100 overflow-hidden flex flex-col rounded-3xl relative border border-white/5 selection:bg-indigo-500/30">


            {/* Old Modal Removed */}

            {/* Premium Background Effects */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-red-600/5 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/4" />
                <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] bg-purple-600/5 rounded-full blur-[100px]" />
            </div>


            {/* Main Content Container */}
            <div className="relative z-10 flex flex-col h-full overflow-hidden">

                {/* Modern Header / Cover Section */}
                <div className="relative pt-12 pb-8 px-8 border-b border-white/5 overflow-hidden">
                    {/* Atmospheric Cover Overlay */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute inset-0 bg-gradient-to-b from-indigo-600/10 via-transparent to-transparent opacity-40 rounded-t-3xl" />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-indigo-500/10 via-transparent to-transparent rounded-t-3xl" />

                        {/* Top Right Close Button (Global) */}
                        <div className="absolute top-6 right-8 z-50">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="rounded-full bg-black/20 hover:bg-white/10 text-zinc-400 hover:text-white transition-all backdrop-blur-sm"
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>

                    <div className="relative z-10 flex flex-col md:flex-row items-center md:items-end gap-8">
                        {/* Enhanced Avatar Display */}
                        <div className="relative group">
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="w-32 h-32 rounded-3xl bg-gradient-to-br from-zinc-800 to-zinc-950 p-1 shadow-2xl relative overflow-hidden ring-1 ring-white/10"
                            >
                                <div className="absolute inset-0 bg-indigo-500/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="w-full h-full rounded-[1.25rem] bg-zinc-900 flex items-center justify-center overflow-hidden relative z-10">
                                    {player.avatar ? (
                                        <img
                                            src={player.avatar.startsWith('http') ? player.avatar : `https://cdn.discordapp.com/avatars/${player.discordId}/${player.avatar}.png?size=256`}
                                            alt={player.name}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex';
                                            }}
                                        />
                                    ) : null}
                                    <User className="fallback-icon w-12 h-12 text-zinc-600" style={{ display: player.avatar ? 'none' : 'flex' }} />
                                </div>
                            </motion.div>

                            {/* Status Badge Over Avatar */}
                            <motion.div
                                initial={{ x: 20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className={`absolute -bottom-2 -right-2 px-3 py-1 rounded-xl text-[10px] font-black tracking-tighter shadow-xl border flex items-center gap-1.5 ${isStaff
                                    ? 'bg-red-500/90 text-white border-red-400/50 backdrop-blur-md'
                                    : 'bg-indigo-500/90 text-white border-indigo-400/50 backdrop-blur-md'
                                    }`}
                            >
                                <div className={`w-2 h-2 rounded-full ${isStaff ? 'bg-white animate-pulse' : 'bg-white'}`} />
                                {isStaff ? 'SISTEMA' : 'IDENTIFICADO'}
                            </motion.div>
                        </div>

                        {/* Player Basic Info */}
                        <div className="flex-1 text-center md:text-left">
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.1 }}
                            >
                                <h2 className="text-3xl md:text-4xl font-black text-white mb-3 tracking-tight drop-shadow-sm break-all line-clamp-1">
                                    {player.name}
                                </h2>

                                <div className="flex flex-wrap justify-center md:justify-start gap-2 items-center">
                                    <Badge className={`${getRoleBadgeColor(player.accessLevel)} bg-white/5 backdrop-blur-md border px-3 py-1 rounded-lg font-bold text-[10px] tracking-wider uppercase`}>
                                        {player.accessLevel || 'Cidadão'}
                                    </Badge>

                                    <div
                                        className="h-7 px-3 rounded-lg bg-zinc-900/50 backdrop-blur-md border border-white/5 flex items-center gap-2 hover:bg-zinc-800/50 transition-all cursor-pointer group"
                                        onClick={handleCopyId}
                                    >
                                        <Hash className="w-3 h-3 text-indigo-400 opacity-70" />
                                        <code className="text-[11px] text-zinc-400 font-mono tracking-tighter truncate max-w-[120px]">{player.discordId}</code>
                                        {idCopied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-zinc-600 group-hover:text-indigo-400 transition-colors" />}
                                    </div>

                                    <div className="h-7 px-3 rounded-lg bg-indigo-500/5 border border-indigo-500/10 flex items-center gap-2 text-zinc-400 text-[11px] font-medium">
                                        <CalendarDays className="w-3 h-3 text-indigo-400/70" />
                                        Desde {formatDate(player.joinDate || player.createdAt)}
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Quick Actions / Status in Header */}
                        <div className="flex flex-wrap justify-center gap-3">
                            <AnimatePresence mode="wait">
                                {!isTicketBanned ? (
                                    <motion.div
                                        key="block-btn"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                    >
                                        <Button
                                            variant="outline"
                                            className="bg-gradient-to-r from-zinc-800 to-black hover:from-zinc-700 hover:to-zinc-900 border-white/10 text-red-400 gap-2 font-bold shadow-xl shadow-black/50"
                                            onClick={() => {
                                                setPunishmentForm({
                                                    ...punishmentForm,
                                                    type: 'Blacklist de Tickets',
                                                    isIndefinite: true
                                                });
                                                setIsPunishModalOpen(true);
                                            }}
                                        >
                                            <Ban className="w-4 h-4" />
                                            Bloquear Tickets
                                        </Button>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="blocked-status"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                    >
                                        {['admin', 'head_staff', 'direcao'].includes(currentUser?.accessLevel) ? (
                                            <Button
                                                variant="outline"
                                                className="border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 gap-2 font-bold shadow-lg shadow-red-900/20"
                                                onClick={() => {
                                                    const ban = activePunishments.find(p => p.punishmentType === 'Blacklist de Tickets' || p.publicReason?.toLowerCase().includes('ticket'));
                                                    if (ban && confirm(`Deseja revogar o bloqueio de tickets de ${player.name}?`)) {
                                                        revokePunishment(ban.id, currentUser?.name, 'Revogação manual via Perfil');
                                                        toast.success('Bloqueio de tickets revogado com sucesso.');
                                                    }
                                                }}
                                            >
                                                <Shield className="w-4 h-4" />
                                                Desbloquear Tickets
                                            </Button>
                                        ) : (
                                            <Button disabled variant="outline" className="border-red-500/30 bg-red-500/5 text-red-400 opacity-50 cursor-not-allowed gap-2">
                                                <Ban className="w-4 h-4" />
                                                Tickets Bloqueados
                                            </Button>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <Button
                                variant="outline"
                                className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 border-none text-white gap-2 font-bold shadow-lg shadow-red-900/20"
                                onClick={() => setIsPunishModalOpen(true)}
                            >
                                <Gavel className="w-4 h-4" />
                                Aplicar Punição
                            </Button>

                            <Button
                                variant="ghost"
                                onClick={onClose}
                                className="h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-zinc-400 hover:text-white hover:bg-white/10 transition-all font-bold gap-2"
                            >
                                <X className="w-4 h-4" />
                                <span className="hidden md:inline">Sair do Perfil</span>
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Scrollable Content */}
                <ScrollArea className="flex-1 p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-10">

                        {/* Left Column: Stats & Info (4 cols) */}
                        <div className="col-span-12 lg:col-span-4 space-y-8">
                            {/* Modern Key Stats Cards */}
                            <div className="grid grid-cols-2 gap-4">
                                <motion.div
                                    whileHover={{ y: -2 }}
                                    className="bg-white/[0.03] backdrop-blur-md border border-white/5 p-5 rounded-2xl flex flex-col items-center justify-center text-center shadow-lg"
                                >
                                    <div className="w-10 h-10 rounded-full bg-indigo-500/10 flex items-center justify-center mb-3">
                                        <Ticket className="w-5 h-5 text-indigo-400" />
                                    </div>
                                    <span className="text-3xl font-black text-white tracking-tighter">{playerTickets.length}</span>
                                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">Total de Tickets</span>
                                </motion.div>

                                <motion.div
                                    whileHover={{ y: -2 }}
                                    className={`bg-white/[0.03] backdrop-blur-md border p-5 rounded-2xl flex flex-col items-center justify-center text-center shadow-lg transition-colors ${isTicketBanned ? 'border-red-500/20 bg-red-500/5' : 'border-white/5'}`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${isTicketBanned ? 'bg-red-500/10' : 'bg-emerald-500/10'}`}>
                                        {isTicketBanned ? <Ban className="w-5 h-5 text-red-500" /> : <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                                    </div>
                                    <span className={`text-base font-black tracking-tighter ${isTicketBanned ? 'text-red-400' : 'text-emerald-400'}`}>
                                        {isTicketBanned ? 'BLOQUEADO' : 'LIBERADO'}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">Criação de Tickets</span>
                                </motion.div>
                            </div>

                            {/* Info Detail Card */}
                            <Card className="bg-white/[0.03] backdrop-blur-md border-white/5 rounded-3xl overflow-hidden shadow-xl">
                                <div className="bg-white/5 px-6 py-4 flex items-center justify-between border-b border-white/5">
                                    <span className="text-[11px] font-black uppercase tracking-[0.2em] text-zinc-400">Informações de Acesso</span>
                                    <Shield className="w-4 h-4 text-indigo-400/50" />
                                </div>
                                <CardContent className="px-6 py-5 space-y-5">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-zinc-500 font-medium">Último Login</span>
                                        <span className="text-xs text-zinc-300 font-bold">{formatDate(player.lastLogin, true)}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-zinc-500 font-medium">Email</span>
                                        <span className="text-xs text-zinc-300 font-bold max-w-[150px] truncate">{player.email || 'Não informado'}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-zinc-500 font-medium">Telefone</span>
                                        <span className="text-xs text-zinc-300 font-bold">{player.phone || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-zinc-500 font-medium">Endereço IP</span>
                                        <Badge className="bg-white/5 border-white/10 text-zinc-500 text-[10px] font-mono">
                                            {player.ip || 'OCULTO'}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center justify-between pt-2 border-t border-white/5">
                                        <span className="text-xs text-zinc-500 font-medium">Membro Desde</span>
                                        <span className="text-xs text-zinc-300 font-bold">{formatDate(player.createdAt || player.joinDate)}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-zinc-500 font-medium">Conta Discord</span>
                                        <div className="flex items-center gap-1 cursor-pointer hover:text-indigo-400 transition-colors" onClick={handleCopyId}>
                                            <span className="text-[10px] text-zinc-400 font-mono tracking-tighter truncate max-w-[100px]">{player.discordId}</span>
                                            {idCopied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3 text-zinc-600" />}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Characters List - Refined Glassmorphism */}
                            <Card className="bg-white/[0.03] backdrop-blur-md border-white/5 rounded-3xl overflow-hidden shadow-xl">
                                <CardHeader className="bg-black/20 pb-4 px-6 border-b border-white/5">
                                    <CardTitle className="text-xs font-black uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-emerald-400" />
                                        Personagens Vinculados
                                    </CardTitle>
                                </CardHeader>
                                <div className="divide-y divide-white/5">
                                    {player.cities?.map((city, idx) => (
                                        <motion.div
                                            key={idx}
                                            whileHover={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
                                            className="p-5"
                                        >
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-black text-white">{city.city}</span>
                                                <Badge className="text-[10px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-mono">
                                                    ID: {city.playerId}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-3 text-zinc-400 group">
                                                <div className="w-8 h-8 rounded-xl bg-zinc-900 border border-white/5 flex items-center justify-center text-zinc-500 group-hover:text-white transition-colors">
                                                    <User className="w-4 h-4" />
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-xs font-bold text-zinc-300 truncate">{city.characterName}</span>
                                                    <span className="text-[10px] text-zinc-500 uppercase tracking-tighter">Residente Ativo</span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                    {(!player.cities || player.cities.length === 0) && (
                                        <div className="p-10 text-center flex flex-col items-center gap-2">
                                            <User className="w-8 h-8 text-zinc-700 mb-2" />
                                            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest italic">Nenhum vínculo detectado</p>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* Summons Card - Warning Aesthetics */}
                            <Card className={`rounded-3xl overflow-hidden border-amber-500/10 ${summons.length > 0 ? 'bg-amber-500/[0.03] border-amber-500/20 shadow-lg shadow-amber-500/5' : 'bg-white/[0.03] border-white/5'}`}>
                                <CardHeader className="bg-black/20 pb-4 px-6 border-b border-amber-500/5">
                                    <CardTitle className="text-xs font-black uppercase tracking-[0.2em] text-amber-500 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        Intimações Relevantes ({summons.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    {summons.length > 0 ? (
                                        <div className="divide-y divide-amber-500/10">
                                            {summons.slice(0, 3).map((summon, idx) => (
                                                <div key={idx} className="p-4 hover:bg-amber-500/10 transition-colors flex justify-between items-center group cursor-pointer" onClick={() => {
                                                    onClose();
                                                    navigate(`/staff/tickets/${summon.id}`);
                                                }}>
                                                    <div className="flex flex-col min-w-0 pr-4">
                                                        <span className="text-sm text-zinc-200 font-bold truncate tracking-tight">{summon.title}</span>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Clock className="w-3 h-3 text-amber-500/50" />
                                                            <span className="text-[10px] text-zinc-500 font-medium">{formatDate(summon.createdAt)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all scale-90 group-hover:scale-100">
                                                        <ExternalLink className="w-4 h-4 text-amber-500" />
                                                    </div>
                                                </div>
                                            ))}
                                            {summons.length > 3 && (
                                                <div className="p-3 bg-amber-500/5 text-center text-[10px] font-black uppercase tracking-widest text-amber-500/60 border-t border-amber-500/10">
                                                    + {summons.length - 3} ocorrências anteriores
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="p-10 text-center">
                                            <p className="text-zinc-600 text-[11px] font-bold uppercase tracking-widest">Nenhuma intimação ativa</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Staff Notes - Deep Glass */}
                            <Card className="bg-white/[0.03] backdrop-blur-md border-white/5 rounded-3xl overflow-hidden flex flex-col h-[280px] shadow-xl">
                                <CardHeader className="bg-black/20 pb-4 px-6 flex flex-row items-center justify-between border-b border-white/5">
                                    <CardTitle className="text-xs font-black uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
                                        <Edit3 className="w-4 h-4 text-indigo-400" />
                                        Relatórios de Gestão
                                    </CardTitle>
                                    {!isEditingNotes ? (
                                        <Button size="sm" variant="outline" onClick={() => setIsEditingNotes(true)} className="h-7 border-indigo-500/20 bg-indigo-500/5 text-[10px] text-indigo-400 font-black uppercase tracking-tighter hover:bg-indigo-500 hover:text-white transition-all">
                                            Editar
                                        </Button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <Button size="sm" variant="ghost" onClick={() => setIsEditingNotes(false)} className="h-7 text-[10px] text-red-500 font-black uppercase tracking-tighter">
                                                Cancelar
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={handleSaveNotes} className="h-7 bg-emerald-500/10 border-emerald-500/30 text-emerald-400 text-[10px] font-black uppercase tracking-tighter hover:bg-emerald-500 hover:text-white">
                                                Salvar <Save className="w-3.5 h-3.5 ml-1" />
                                            </Button>
                                        </div>
                                    )}
                                </CardHeader>
                                <CardContent className="flex-1 p-0">
                                    {isEditingNotes ? (
                                        <Textarea
                                            value={tempNotes}
                                            onChange={(e) => setTempNotes(e.target.value)}
                                            className="w-full h-full bg-zinc-950/20 border-0 resize-none p-6 focus-visible:ring-0 text-sm text-zinc-200 leading-relaxed font-medium"
                                            placeholder="Descreva observações comportamentais, conduta em tickets..."
                                            autoFocus
                                        />
                                    ) : (
                                        <ScrollArea className="h-full">
                                            <div className="p-6 text-sm text-zinc-400 whitespace-pre-wrap leading-relaxed">
                                                {staffNotes || <div className="h-full flex flex-col items-center justify-center opacity-40 mt-10">
                                                    <ScrollText className="w-8 h-8 mb-2" />
                                                    <span className="text-[10px] uppercase font-black tracking-widest">Vazio</span>
                                                </div>}
                                            </div>
                                        </ScrollArea>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Right Column: Tickets & History (8 cols) */}
                        <div className="col-span-12 lg:col-span-8 space-y-8">

                            {/* Active Punishments Alert - Critical Warning */}
                            {activePunishments.length > 0 && (
                                <div className="space-y-4">
                                    {activePunishments.map((p, idx) => (
                                        <motion.div
                                            key={idx}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="bg-[#18181b] border border-white/5 rounded-2xl p-6 shadow-xl relative overflow-hidden group"
                                        >
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative z-10">
                                                {/* Left Column: Punishment Details */}
                                                <div className="space-y-6">
                                                    <div>
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 block mb-1">TIPO DE PUNIÇÃO</span>
                                                        <h3 className="text-2xl font-black text-white tracking-tight">{p.punishmentType || p.type}</h3>
                                                    </div>

                                                    <div className="flex items-start gap-12">
                                                        <div>
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 block mb-1">DURAÇÃO</span>
                                                            <span className="text-sm font-bold text-zinc-200">
                                                                {p.expiresAt ? (
                                                                    formatDate(p.expiresAt)
                                                                ) : (
                                                                    'Permanente'
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 block mb-1">APLICAÇÃO</span>
                                                            <span className="text-sm font-bold text-zinc-200 block">{formatDate(p.appliedAt)}</span>
                                                            <span className="text-[10px] text-zinc-500 font-medium lowercase">às {new Date(p.appliedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                                        </div>
                                                    </div>

                                                    <div className="bg-[#27272a]/50 border border-white/5 rounded-xl p-4 mt-2">
                                                        <div className="grid grid-cols-3 divide-x divide-white/5 text-center">
                                                            <div className="px-2">
                                                                <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 block mb-1">CIDADE</span>
                                                                <span className="text-xs font-bold text-indigo-400">{p.city || 'GLOBAL'}</span>
                                                            </div>
                                                            <div className="px-2">
                                                                <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 block mb-1">PERSONAGEM</span>
                                                                <span className="text-xs font-bold text-white truncate block">{p.characterName || 'N/A'}</span>
                                                            </div>
                                                            <div className="px-2">
                                                                <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 block mb-1">IDENTIFICADOR</span>
                                                                <Badge variant="outline" className="border-amber-500/20 text-amber-500 text-[10px] font-mono">
                                                                    #{p.characterId || '?'}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right Column: Reasons & Actions */}
                                                <div className="flex flex-col h-full border-l border-white/5 pl-0 lg:pl-8">
                                                    <div className="flex items-center justify-between mb-6">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">RESPONSÁVEL:</span>
                                                            <Badge variant="secondary" className="bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 border-0 font-bold text-[10px]">
                                                                {p.appliedBy}
                                                            </Badge>
                                                        </div>
                                                        <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[10px] font-black uppercase px-3">
                                                            ATIVA
                                                        </Badge>
                                                    </div>

                                                    <div className="space-y-4 mb-6 flex-1">
                                                        <div>
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 block mb-2">MOTIVO PÚBLICO</span>
                                                            <div className="bg-black/20 border border-white/5 rounded-xl p-3 text-sm text-zinc-300 italic">
                                                                "{p.reason || p.publicReason}"
                                                            </div>
                                                        </div>

                                                        {p.internalReason && (
                                                            <div>
                                                                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 block mb-2">NOTAS INTERNAS & PROVAS</span>
                                                                <div className="bg-black/20 border border-white/5 rounded-xl p-3 text-xs text-zinc-400 font-mono whitespace-pre-wrap max-h-[80px] overflow-y-auto custom-scrollbar">
                                                                    {p.internalReason}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="space-y-3 mt-auto pt-4 border-t border-white/5">
                                                        <Button
                                                            variant="outline"
                                                            className="w-full border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30 font-bold tracking-tight h-10"
                                                            onClick={() => {
                                                                if (confirm('Tem certeza que deseja revogar esta punição?')) {
                                                                    revokePunishment(p.id, currentUser?.name, 'Revogação manual via botão de ação rápida');
                                                                    toast.success('Punição revogada com sucesso.');
                                                                }
                                                            }}
                                                        >
                                                            <RotateCcw className="w-3.5 h-3.5 mr-2" />
                                                            Revogar Punição
                                                        </Button>

                                                        {['admin', 'head_staff', 'direcao'].includes(currentUser?.accessLevel) && (
                                                            <button
                                                                onClick={() => handleDeletePunishment(p.id)}
                                                                className="w-full flex items-center justify-center gap-2 text-[10px] uppercase font-bold tracking-widest text-red-500/50 hover:text-red-500 transition-colors py-2"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                                Apagar Histórico permanentemente
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}    </div>
                            )}

                            {/* Tickets List - Modernized Area */}
                            <Card className="bg-white/[0.03] backdrop-blur-md border-white/5 rounded-[2.5rem] h-[650px] flex flex-col shadow-2xl overflow-hidden relative">
                                <CardHeader className="bg-zinc-900/40 border-b border-white/5 px-8 py-6">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-sm font-black uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-3">
                                            <div className="p-2 bg-indigo-500/10 rounded-xl">
                                                <History className="w-5 h-5 text-indigo-400" />
                                            </div>
                                            Fluxo de Tickets & Demandas ({playerTickets.length})
                                        </CardTitle>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2 group cursor-help">
                                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                                                <span className="text-[10px] font-black tracking-tighter text-zinc-500 group-hover:text-emerald-400 transition-colors uppercase">Em Aberto</span>
                                            </div>
                                            <div className="flex items-center gap-2 group cursor-help">
                                                <div className="w-2.5 h-2.5 rounded-full bg-zinc-600"></div>
                                                <span className="text-[10px] font-black tracking-tighter text-zinc-500 group-hover:text-zinc-300 transition-colors uppercase">Finalizado</span>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-0 flex-1 overflow-hidden bg-zinc-950/10">
                                    <ScrollArea className="h-full">
                                        {playerTickets.length > 0 ? (
                                            <div className="divide-y divide-white/5">
                                                {playerTickets.map((ticket) => (
                                                    <motion.div
                                                        key={ticket.id}
                                                        whileHover={{ backgroundColor: 'rgba(255,255,255,0.01)' }}
                                                        className="px-8 py-5 transition-all group flex items-center justify-between gap-6"
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center flex-wrap gap-3 mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <div className={`w-2 h-2 rounded-full ${ticket.status === 'fechado' || ticket.status === 'resolvido' ? 'bg-zinc-600' : 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.3)]'}`} />
                                                                    <span className="text-xs font-black text-indigo-400/70 font-mono">#{ticket.id}</span>
                                                                </div>
                                                                <h4 className={`text-sm font-bold tracking-tight truncate max-w-[400px] ${ticket.status === 'fechado' || ticket.status === 'resolvido' ? 'text-zinc-500' : 'text-zinc-100'}`}>
                                                                    {ticket.title}
                                                                </h4>
                                                                {ticket.category === 'escalacao_interna' && (
                                                                    <Badge className="bg-amber-500/10 text-amber-500 text-[9px] font-black tracking-[0.1em] border-amber-500/20 px-2 h-5 rounded-md">
                                                                        ESCALADO
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center flex-wrap gap-5">
                                                                <div className="flex items-center gap-1.5 text-zinc-500">
                                                                    <Clock className="w-3.5 h-3.5 text-zinc-600" />
                                                                    <span className="text-[10px] font-bold uppercase tracking-tighter">{formatDate(ticket.createdAt, true)}</span>
                                                                </div>
                                                                <div className="flex items-center gap-1.5 text-zinc-500">
                                                                    <Hash className="w-3.5 h-3.5 text-zinc-600" />
                                                                    <span className="text-[10px] font-bold uppercase tracking-tighter">{ticket.category}</span>
                                                                </div>
                                                                {(ticket.status === 'fechado' || ticket.status === 'resolvido') && (
                                                                    <div className="flex items-center gap-1.5 text-emerald-500/70">
                                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                                        <span className="text-[10px] font-black uppercase tracking-tighter">Resolvido</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                            <Button
                                                                size="sm"
                                                                className="opacity-0 group-hover:opacity-100 transition-all bg-indigo-500/10 text-indigo-400 hover:bg-indigo-600 hover:text-white border border-indigo-500/30 font-black text-[10px] uppercase tracking-tighter rounded-xl h-9 px-4 gap-2"
                                                                onClick={() => {
                                                                    onClose();
                                                                    navigate(`/staff/tickets/${ticket.id}`);
                                                                }}
                                                            >
                                                                Dossiê
                                                                <ExternalLink className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </motion.div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-full text-zinc-600 gap-4 p-20">
                                                <div className="w-20 h-20 rounded-[2rem] bg-zinc-900 border border-white/5 flex items-center justify-center opacity-20">
                                                    <MessageSquare className="w-10 h-10" />
                                                </div>
                                                <p className="text-[11px] font-black uppercase tracking-[0.3em] text-center">Inexistência de registros</p>
                                            </div>
                                        )}
                                    </ScrollArea>
                                </CardContent>
                                <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[#121214] to-transparent pointer-events-none opacity-50" />
                            </Card>


                            {/* Full Punishment History Section - Ultra Glass */}
                            <div className="space-y-6 pt-6 pb-12">
                                <div className="flex items-center justify-between px-2">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-red-500/10 rounded-xl">
                                            <Gavel className="w-5 h-5 text-red-500" />
                                        </div>
                                        <h2 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-400">Histórico de Punições</h2>
                                    </div>
                                    <Badge className="bg-white/5 border-white/10 text-zinc-500 text-[10px] font-bold">
                                        {allPlayerPunishments.length} REGISTROS
                                    </Badge>
                                </div>

                                {allPlayerPunishments.length > 0 ? (
                                    <div className="space-y-4">
                                        {allPlayerPunishments.map((p) => {
                                            const isRevoked = p.status === 'revoked';
                                            const isExpired = p.expiresAt && new Date(p.expiresAt) < new Date();
                                            const isActive = !isRevoked && !isExpired;

                                            return (
                                                <motion.div
                                                    key={p.id}
                                                    whileHover={{ scale: 1.005 }}
                                                    className={`bg-white/[0.03] backdrop-blur-md border rounded-3xl p-6 shadow-xl relative overflow-hidden group transition-all ${isRevoked ? 'border-zinc-800 opacity-60' : isActive ? 'border-red-500/20' : 'border-white/5'}`}
                                                >
                                                    {isRevoked && (
                                                        <div className="absolute top-0 right-0 p-12 bg-zinc-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                                                    )}
                                                    <div className="relative z-10">
                                                        <div className="flex flex-wrap items-center justify-between gap-4 mb-5 pb-5 border-b border-white/5">
                                                            <div className="flex items-center gap-4">
                                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isRevoked ? 'bg-zinc-500/10 text-zinc-500' : 'bg-red-500/10 text-red-500'}`}>
                                                                    <FileText className="w-5 h-5" />
                                                                </div>
                                                                <div>
                                                                    <h3 className={`text-sm font-black tracking-tight ${isRevoked ? 'text-zinc-500 line-through' : 'text-zinc-100'}`}>
                                                                        {p.punishmentType || p.type}
                                                                    </h3>
                                                                    <div className="flex items-center gap-2 mt-1">
                                                                        <Clock className="w-3 h-3 text-zinc-600" />
                                                                        <span className="text-[10px] uppercase font-black tracking-widest text-zinc-500">
                                                                            {formatDate(p.appliedAt)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {isRevoked ? (
                                                                    <Badge className="bg-zinc-800 text-zinc-500 border-zinc-700 text-[9px] font-black tracking-widest uppercase px-3">REVOGADA</Badge>
                                                                ) : isActive ? (
                                                                    <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[9px] font-black tracking-widest uppercase px-3 animate-pulse">ATIVA</Badge>
                                                                ) : (
                                                                    <Badge className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20 text-[9px] font-black tracking-widest uppercase px-3">EXPIRADA</Badge>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                            <div className="space-y-4">
                                                                <div>
                                                                    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 block mb-2">Descrição dos Fatos</span>
                                                                    <p className="text-zinc-300 text-sm leading-relaxed bg-black/30 p-4 rounded-2xl border border-white/5 break-all">
                                                                        {p.reason || p.publicReason}
                                                                    </p>
                                                                </div>

                                                                <div className="flex items-center gap-6">
                                                                    <div>
                                                                        <span className="text-[9px] font-black uppercase tracking-widest text-zinc-600 block mb-1">Responsável</span>
                                                                        <span className="text-xs text-zinc-400 font-bold">{p.appliedBy}</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-[9px] font-black uppercase tracking-widest text-zinc-600 block mb-1">Vigência Ativa</span>
                                                                        <span className="text-xs font-black text-zinc-300">
                                                                            {p.expiresAt ? formatDate(p.expiresAt) : 'PERMANENTE'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-4">
                                                                <div>
                                                                    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 block mb-2">Evidências e Provas</span>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {p.evidence && p.evidence.length > 0 ? (
                                                                            p.evidence.map((url, i) => (
                                                                                <motion.a
                                                                                    key={i}
                                                                                    whileHover={{ scale: 1.05 }}
                                                                                    whileTap={{ scale: 0.95 }}
                                                                                    href={url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="h-9 px-4 rounded-xl bg-indigo-500/5 border border-indigo-500/20 flex items-center gap-2 text-[10px] font-black text-indigo-400 uppercase tracking-tighter hover:bg-indigo-500 hover:text-white transition-all shadow-lg shadow-indigo-500/5"
                                                                                >
                                                                                    <FileVideo className="w-3.5 h-3.5" />
                                                                                    PROVA #{i + 1}
                                                                                </motion.a>
                                                                            ))
                                                                        ) : (
                                                                            <div className="h-9 px-4 rounded-xl bg-zinc-900/50 border border-white/5 flex items-center text-[10px] font-black text-zinc-600 uppercase tracking-widest italic">
                                                                                Sem mídia anexada
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {isRevoked && (
                                                                    <div className="p-4 bg-zinc-900/40 rounded-2xl border border-white/5">
                                                                        <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 block mb-1">Motivo da Revogação</span>
                                                                        <p className="text-xs text-zinc-400 italic">"{p.revocationReason || 'Nenhum motivo detalhado'}"</p>
                                                                        <div className="mt-2 text-[9px] text-zinc-600 font-bold uppercase">Por: {p.revokedBy}</div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="bg-white/[0.02] border border-dashed border-white/10 rounded-3xl p-12 text-center flex flex-col items-center gap-4">
                                        <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center opacity-40">
                                            <ShieldAlert className="w-8 h-8 text-zinc-500" />
                                        </div>
                                        <div>
                                            <p className="text-zinc-400 font-black text-xs uppercase tracking-[0.2em] mb-1">Histórico Impecável</p>
                                            <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider">Este cidadão não possui registros de conduta adversa</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </ScrollArea>
            </div>

            {/* Redesigned Punishment Modal Content */}
            <Dialog open={isPunishModalOpen} onOpenChange={setIsPunishModalOpen}>
                <DialogContent
                    className="bg-[#09090b] border-white/5 p-0 max-w-2xl overflow-hidden rounded-[2.5rem] shadow-2xl relative"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                >
                    <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-red-600 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
                    </div>

                    <div className="relative z-10 flex flex-col h-full max-h-[90vh]">
                        <div className="p-8 border-b border-white/5 bg-black/20">
                            <h2 className="text-xl font-black text-white flex items-center gap-3 tracking-tight">
                                <div className="p-2 bg-red-500/10 rounded-xl">
                                    <Gavel className="w-6 h-6 text-red-500" />
                                </div>
                                Aplicar Nova Medida Disciplinar
                            </h2>
                            <p className="text-zinc-500 text-xs font-bold uppercase tracking-[0.1em] mt-2">
                                Jogador: <span className="text-zinc-300">{player.name}</span>
                            </p>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 space-y-6">
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 px-1">Tipo de Punição</label>
                                        <Select
                                            value={punishmentForm.type}
                                            onValueChange={(val) => setPunishmentForm({ ...punishmentForm, type: val })}
                                        >
                                            <SelectTrigger className="bg-white/5 border-white/10 rounded-xl h-12 text-zinc-200 w-full overflow-hidden">
                                                <SelectValue placeholder="Selecione o tipo..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-white/10 z-[100]">
                                                {PUNISHMENT_TYPES.map(type => (
                                                    <SelectItem key={type} value={type} className="text-white hover:bg-white/10">{type}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 px-1">Perfil Alvo</label>
                                        <Select
                                            value={punishmentForm.selectedCharacterIdx}
                                            onValueChange={(val) => setPunishmentForm({ ...punishmentForm, selectedCharacterIdx: val })}
                                        >
                                            <SelectTrigger className="bg-white/5 border-white/10 rounded-xl h-12 text-zinc-200 w-full overflow-hidden">
                                                <SelectValue placeholder="Selecione o perfil" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-white/10 z-[100]">
                                                <SelectItem value="all" className="text-white hover:bg-white/10">Global (Discord ID)</SelectItem>
                                                {player.cities?.map((city, idx) => (
                                                    <SelectItem key={idx} value={idx.toString()} className="text-white hover:bg-white/10">
                                                        {city.city} (ID: {city.playerId})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Duration Fields (Only if not Advertencia) */}
                                {punishmentForm.type !== 'Advertência' && (
                                    <div className="space-y-2 bg-white/5 p-4 rounded-xl border border-white/5">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Duração da Pena</label>
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    id="indefinite"
                                                    checked={punishmentForm.isIndefinite}
                                                    onCheckedChange={(checked) => setPunishmentForm({ ...punishmentForm, isIndefinite: checked })}
                                                    className="border-white/20 data-[state=checked]:bg-indigo-500"
                                                />
                                                <label htmlFor="indefinite" className="text-xs text-zinc-300 font-bold cursor-pointer select-none">Permanente</label>
                                            </div>
                                        </div>

                                        {!punishmentForm.isIndefinite && (
                                            <div className="flex gap-2">
                                                <Input
                                                    type="number"
                                                    placeholder="Duração"
                                                    value={punishmentForm.duration}
                                                    onChange={(e) => setPunishmentForm({ ...punishmentForm, duration: e.target.value })}
                                                    className="bg-black/20 border-white/10 h-11 flex-1 text-zinc-200"
                                                />
                                                <Select
                                                    value={punishmentForm.durationType}
                                                    onValueChange={(val) => setPunishmentForm({ ...punishmentForm, durationType: val })}
                                                >
                                                    <SelectTrigger className="bg-black/20 border-white/10 h-11 w-32 text-zinc-200">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-zinc-900 border-white/10 z-[100]">
                                                        <SelectItem value="days" className="text-white">Dias</SelectItem>
                                                        <SelectItem value="hours" className="text-white">Horas</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 px-1">Motivo (Público)</label>
                                    <Input
                                        value={punishmentForm.reason}
                                        onChange={(e) => setPunishmentForm({ ...punishmentForm, reason: e.target.value })}
                                        className="bg-white/5 border-white/10 rounded-xl h-12 focus-visible:ring-indigo-500/30 text-zinc-200 px-4"
                                        placeholder="Motivo visível para o jogador..."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 px-1">Observações Internas (Staff)</label>
                                    <Textarea
                                        value={punishmentForm.notes}
                                        onChange={(e) => setPunishmentForm({ ...punishmentForm, notes: e.target.value })}
                                        className="bg-white/5 border-white/10 rounded-2xl min-h-[100px] focus-visible:ring-indigo-500/30 text-zinc-200 p-4 resize-none"
                                        placeholder="Detalhes técnicos, links ou contexto adicional..."
                                    />
                                </div>

                                <div className="space-y-4">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 px-1">Anexar Provas (R2 Cloud)</label>
                                    <R2Upload
                                        folderPath={`evidence/profiles/${player.discordId}/${punishmentForm.type.toLowerCase().replace(/\s+/g, '_')}`}
                                        onUploadComplete={(paths) => {
                                            const newPaths = Array.isArray(paths) ? paths : [paths];
                                            setPunishmentForm(prev => ({
                                                ...prev,
                                                attachments: [...prev.attachments, ...newPaths]
                                            }));
                                        }}
                                        maxFiles={5}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="p-8 border-t border-white/5 bg-black/20 flex gap-4">
                            <Button
                                variant="ghost"
                                className="flex-1 h-12 rounded-2xl text-zinc-500 hover:text-white"
                                onClick={() => setIsPunishModalOpen(false)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                className="flex-[2] h-12 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-black uppercase tracking-widest text-xs shadow-lg shadow-indigo-600/20"
                                onClick={handlePunishSubmit}
                                disabled={isApplyingPunishment || !punishmentForm.reason}
                            >
                                {isApplyingPunishment ? (
                                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processando...</>
                                ) : (
                                    <><Gavel className="w-4 h-4 mr-2" /> Protocolar Punição</>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PlayerProfileView;
